import React, { useState } from 'react';
import {
    Box,
    Dialog,
    DialogContent,
    IconButton,
    Link,
    Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import ImageNotSupportedOutlinedIcon from '@mui/icons-material/ImageNotSupportedOutlined';
import { useTranslation } from 'react-i18next';
import { formatDateForDisplay } from '../../../utils';

export interface ReturnPhotoItem {
    key: string;
    url: string;
    uploadedAt: string | null;
}

interface ReturnPhotoThumbnailProps {
    photos: ReturnPhotoItem[];
    /** 'uploaded' | 'skipped' | 'failed' | null — drives the empty-state note. */
    status?: string | null;
    isLoading: boolean;
    isError: boolean;
    // When true, render an informational placeholder instead of nothing once the
    // query has resolved with no photo (e.g. the customer tapped "skip").
    showEmptyState?: boolean;
    // Title label above the thumbnail. Defaults to the return-photos label; the
    // generic check-in/checkout consumer passes a check-out-photo label.
    titleKey?: string;
    /** #3219: the booking location's zone for the upload time; the station default when omitted. */
    timeZone?: string;
}

// Shared frame so the photo, the empty-state note and the error note all keep
// the same footprint in the booking-details header column.
const FRAME_CLASSES = `
    w-[128px] h-[128px] rounded-lg border border-dashed border-gray-300
    bg-white flex flex-col items-center justify-center gap-2 px-3 text-center
`;

/**
 * Presentational return-photo thumbnail: a 128×128 thumbnail (opening a
 * full-screen dialog) with a titled empty/error state. Shared by
 * `RentalReturnPhotoThumbnail` (SUP/rental, rental table) and
 * `UsageLogReturnPhotoThumbnail` (generic check-in/checkout, usage_log) so both
 * surfaces render identically. Fetching lives in the wrappers.
 */
const ReturnPhotoThumbnail: React.FC<ReturnPhotoThumbnailProps> = ({
    photos,
    status,
    isLoading,
    isError,
    showEmptyState = false,
    titleKey = 'booking_details.return_photos.title',
    timeZone,
}) => {
    const { t } = useTranslation();
    // Single shared dialog: the open photo's identity drives what's shown.
    const [openPhoto, setOpenPhoto] = useState<ReturnPhotoItem | null>(null);

    if (photos.length === 0) {
        // Nothing to say yet (still loading) or the caller doesn't want a
        // placeholder for this use case: render nothing, as before.
        if (isLoading || !showEmptyState) {
            return null;
        }

        // Pick the most specific reason available. The backend status (when
        // present) distinguishes a deliberate skip from a failed upload; older
        // rows without the field fall back to the generic "no photo" note.
        const emptyStateMessageKey = isError
            ? 'booking_details.return_photos.error'
            : status === 'failed'
                ? 'booking_details.return_photos.failed'
                : status === 'skipped'
                    ? 'booking_details.return_photos.skipped'
                    : 'booking_details.return_photos.none';

        return (
            <div className={'flex flex-col items-center'}>
                <span
                    className={`
                        text-[11px] uppercase tracking-wider font-medium
                        text-gray-500 mb-2 text-center
                    `}
                >
                    {t(titleKey)}
                </span>
                <div className={FRAME_CLASSES}>
                    <ImageNotSupportedOutlinedIcon fontSize={'small'} className={'text-gray-400'} />
                    <span className={'text-xs text-gray-500'}>
                        {t(emptyStateMessageKey)}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={'flex flex-col items-center'}>
                <span
                    className={`
                        text-[11px] uppercase tracking-wider font-medium
                        text-gray-500 mb-2 text-center
                    `}
                >
                    {t(titleKey)}
                </span>
                <div className={'flex flex-row flex-wrap items-start gap-3 justify-center'}>
                    {photos.map((photo) => (
                        <button
                            key={photo.key}
                            type={'button'}
                            onClick={() => setOpenPhoto(photo)}
                            className={`
                                group flex flex-col items-center shrink-0
                                rounded-lg
                                focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400
                            `}
                        >
                            <div
                                className={`
                                    relative w-[128px] h-[128px] overflow-hidden rounded-lg
                                    bg-white ring-1 ring-gray-200 shadow-sm
                                    transition-all duration-200
                                    group-hover:shadow-md group-hover:ring-gray-300
                                `}
                            >
                                <img
                                    src={photo.url}
                                    alt={t(titleKey)}
                                    className={`
                                        w-full h-full object-cover
                                        transition-transform duration-300
                                        group-hover:scale-105
                                    `}
                                    loading={'lazy'}
                                />
                                <span
                                    className={`
                                        absolute inset-0 flex items-center justify-center
                                        bg-black/0 transition-colors duration-200
                                        group-hover:bg-black/30
                                    `}
                                >
                                    <OpenInFullIcon
                                        fontSize={'small'}
                                        className={`
                                            text-white opacity-0 transition-opacity duration-200
                                            group-hover:opacity-100
                                        `}
                                    />
                                </span>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <Dialog
                open={openPhoto !== null}
                onClose={() => setOpenPhoto(null)}
                maxWidth={false}
                PaperProps={{
                    sx: {
                        m: 2,
                        maxHeight: 'calc(100vh - 32px)',
                        maxWidth: 'calc(100vw - 32px)',
                    },
                }}
            >
                <DialogContent
                    sx={{
                        p: 0,
                        position: 'relative',
                        display: 'flex',
                        flexDirection: 'column',
                        overflow: 'hidden',
                    }}
                >
                    <IconButton
                        onClick={() => setOpenPhoto(null)}
                        sx={{
                            position: 'absolute',
                            top: 8,
                            right: 8,
                            backgroundColor: 'rgba(255,255,255,0.85)',
                            '&:hover': { backgroundColor: 'rgba(255,255,255,1)' },
                        }}
                        aria-label={t('common.close')}
                    >
                        <CloseIcon />
                    </IconButton>
                    {openPhoto && (
                        <>
                            <img
                                src={openPhoto.url}
                                alt={t(titleKey)}
                                style={{
                                    display: 'block',
                                    minHeight: 0,
                                    maxWidth: '100%',
                                    maxHeight: 'calc(100vh - 32px - 44px)',
                                    width: 'auto',
                                    height: 'auto',
                                    objectFit: 'contain',
                                    margin: '0 auto',
                                }}
                            />
                            <Box className={'flex justify-between items-center px-4 py-2 shrink-0'}>
                                <Typography variant={'body2'} className={'text-gray-500'}>
                                    {openPhoto.uploadedAt ? formatDateForDisplay(openPhoto.uploadedAt, timeZone) : ''}
                                </Typography>
                                <Link
                                    href={openPhoto.url}
                                    target={'_blank'}
                                    rel={'noopener noreferrer'}
                                    underline={'hover'}
                                    color={'inherit'}
                                    className={'text-sm'}
                                >
                                    {t('booking_details.return_photos.open_full')}
                                </Link>
                            </Box>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
};

export default ReturnPhotoThumbnail;
